import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Target, Lightbulb, Sparkles, ChevronLeft, ChevronRight,
  Linkedin, Instagram, Twitter, Facebook 
} from 'lucide-react';
import { theme } from '../../utils/theme';
import { useLanguage } from '../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const INSTAGRAM_URL = "https://www.instagram.com/sailectech?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==";

// --- HOOKS & ANIMATIONS ---

const useScrollReveal = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );
    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);

  return [ref, isVisible];
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- SOUS-COMPOSANTS ---

const MissionCore = ({ mousePosition, t }) => {
  const x = mousePosition?.x || 0;
  const y = mousePosition?.y || 0;
  const rotateX = (y - window.innerHeight / 2) / 50;
  const rotateY = (x - window.innerWidth / 2) / 50;

  return (
    <div className="relative min-h-[500px] flex items-center justify-center overflow-hidden bg-black perspective-1000 border-t" 
      style={{ borderColor: theme.colors.border, backgroundColor: 'rgba(255,255,255,0.01)' }}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(231,165,95,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 w-full max-w-5xl px-4 flex flex-col md:flex-row items-center justify-between gap-20">
        <div className="relative w-64 h-64 md:w-96 md:h-96 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border border-orange-500/20 animate-spin-slow" style={{ borderStyle: 'dashed' }}></div>
            <div className="absolute inset-8 rounded-full border border-teal-500/20 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '15s' }}></div>
            <div className="absolute inset-16 rounded-full border border-white/10 animate-spin-slow" style={{ animationDuration: '20s' }}></div>
            
            <div className="w-32 h-32 rounded-full blur-xl animate-pulse opacity-80 relative z-10"
                 style={{ background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})` }}></div>

            <div className="absolute inset-0 transition-transform duration-200 ease-out" 
                 style={{ transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)` }}>
               <div className="absolute top-0 right-0 w-4 h-4 bg-teal-400 rounded-full blur-sm"></div>
               <div className="absolute bottom-10 left-10 w-2 h-2 bg-orange-400 rounded-full blur-sm"></div>
               <div className="absolute top-1/2 -right-10 w-3 h-3 bg-white rounded-full blur-md"></div>
            </div>
        </div>

        <div className="flex-1 text-left">
           <div className="inline-block px-3 py-1 mb-4 border border-orange-400 rounded-full text-xs font-bold tracking-widest bg-teal-900/10" style={{ color: theme.colors.accent }}>
             {t.about.missionBadge}
           </div>
           <h2 className="text-3xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
             {t.about.missionTitle1}<br/>{t.about.missionTitle2}
           </h2>
           <div className="h-1 w-24 mb-8" style={{ background: `linear-gradient(90deg, ${theme.colors.primary}, transparent)` }}></div>
           <p className="text-xl text-gray-400 font-light leading-relaxed">
             {t.about.missionDescription}
           </p>
        </div>
      </div>
    </div>
  );
};

const HolographicValue = ({ title, icon, desc, color }) => {
  return (
    <div className="group relative flex-1 min-w-[100px] h-[400px] transition-all duration-500 ease-out hover:flex-[2] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a]">
      {/* Background Gradient on Hover */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500`} style={{backgroundColor: color}}></div>
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.05]" 
           style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

      {/* Content Container */}
      <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
        {/* Icon Floating */}
        <div className="absolute top-8 left-8 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md group-hover:scale-110 transition-transform duration-500 group-hover:bg-white/10 group-hover:border-white/20">
           {React.cloneElement(icon, { size: 32, className: "text-white" })}
        </div>

        {/* Vertical Text (Default State) */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 rotate-90 origin-center text-gray-500 font-bold tracking-[0.3em] uppercase opacity-100 group-hover:opacity-0 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
          {title}
        </div>

        {/* Expanded Content (Hover State) */}
        <div className="transform translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-100">
          <h3 className="text-xl md:text-3xl font-bold text-white mb-4 leading-tight">{title}</h3>
          <p className="text-gray-300 text-sm leading-relaxed border-l-2 pl-4" style={{ borderColor: theme.colors.primary }}>{desc}</p>
        </div>
      </div>

      {/* Glowing Border Bottom */}
      <div className={`absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 `} style={{ backgroundColor: color }}></div>
    </div>
  );
};

const TimelineItem = ({ year, title, desc, isHighlight, index }) => {
  return (
    <Reveal delay={index * 100}>
      <div className="relative pl-8 md:pl-12 group">
        <div 
          className={`absolute -left-[5px] top-6 w-3 h-3 rounded-full border-2 bg-black z-10 transition-all duration-500
                      group-hover:scale-150 group-hover:border-orange-500 group-hover:shadow-[0_0_10px_#E7A55F]
                      ${isHighlight ? 'border-orange-500 shadow-[0_0_15px_rgba(231,165,95,0.4)] scale-125' : 'border-gray-600'}`}
          style={{ borderColor: isHighlight ? theme.colors.primary : undefined }}
        >
          {isHighlight && <div className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-20"></div>}
        </div>
        
        <div className="absolute left-[5px] top-[30px] h-px w-0 bg-orange-500 group-hover:w-8 transition-all duration-500 opacity-50"></div>

        <div className={`p-6 rounded-2xl border transition-all duration-500 transform group-hover:translate-x-2 group-hover:bg-white/5 
                        ${isHighlight ? 'bg-orange-500/5 border-orange-500/30' : 'bg-[#111] border-white/5 group-hover:border-orange-500/30'}`}>
          <div className="flex items-center justify-between mb-2">
             <span className="text-xs font-bold uppercase tracking-widest opacity-60 transition-colors group-hover:text-orange-400" 
                   style={{ color: isHighlight ? theme.colors.primary : theme.colors.textMuted }}>
               {year}
             </span>
             {isHighlight && <Sparkles size={14} className="text-orange-500 animate-pulse" />}
          </div>
          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-100 transition-colors">{title}</h3>
          <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">{desc}</p>
        </div>
      </div>
    </Reveal>
  );
};

const TeamCard = ({ member, language }) => {
  const [imageError, setImageError] = useState(false);
  
  const name = member.name;
  const role = language === 'en' ? (member.role_en || member.role) : member.role;
  const quote = language === 'en' ? (member.quote_en || member.quote) : member.quote;
  const description = language === 'en' ? (member.description_en || member.description) : member.description;
  
  const imageUrl = imageError || !member.image 
    ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.id || member.name}&backgroundColor=b6e3f4`
    : member.image;
  
  return (
    <div className="group relative bg-[#111] rounded-3xl overflow-hidden border border-white/5 hover:border-orange-500/50 transition-all duration-500 h-full flex flex-col">
      <div className="h-64 bg-gray-800 relative overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-gradient-to-t from-[#111] to-transparent z-10"></div>
        <img 
          src={imageUrl} 
          alt={name} 
          className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-700"
          onError={() => setImageError(true)}
        />
      </div>
      <div className="p-8 relative z-20 -mt-12 flex flex-col flex-grow">
         <div className="w-16 h-1 bg-orange-500 mb-6 rounded-full"></div>
         <h3 className="text-2xl font-bold text-white mb-1">{name}</h3>
         <p className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: theme.colors.secondary }}>{role}</p>
         <div className="mb-6 relative">
           <span className="absolute -left-2 -top-2 text-4xl text-gray-700 font-bold leading-none">"</span>
           <p className="text-white italic relative z-10 pl-4">{quote}</p>
         </div>
         <p className="text-gray-400 text-sm leading-relaxed mt-auto">{description}</p>
      </div>
    </div>
  );
};

const SocialButton = ({ icon, label, href }) => {
  const handleClick = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <button 
      onClick={handleClick}
      className="flex items-center gap-3 px-6 py-4 rounded-full bg-[#151515] border border-white/5 hover:bg-white/10 hover:border-orange-500/50 hover:text-orange-500 hover:-translate-y-1 transition-all duration-300 group"
    >
      <span className="group-hover:scale-110 transition-transform">{icon}</span>
      <span className="font-bold hidden md:inline">{label}</span>
    </button>
  );
};

// --- COMPOSANT PRINCIPAL ---

export const AboutPage = () => {
  const { t, language } = useLanguage();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setParallax({
        x: (e.clientX - window.innerWidth / 2) / 40,
        y: (e.clientY - window.innerHeight / 2) / 40
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/team`);
        // CORRECTION 1: On vérifie que response.data est bien une liste
        if (Array.isArray(response.data)) {
            setTeamMembers(response.data);
        } else {
            console.warn("API Team format invalide:", response.data);
            setTeamMembers([]); // Ou utiliser le fallback ci-dessous
            throw new Error("Format invalide"); // Pour déclencher le catch
        }
      } catch (error) {
        console.error('Error fetching team:', error);
        setTeamMembers([
          { id: 'marie-laure', name: 'Marie-Laure Boivin', role: 'CEO & Co-fondatrice', role_en: 'CEO & Co-founder', quote: "L'innovation, c'est de la désobéissance qui a réussi.", quote_en: "Innovation is disobedience that succeeded.", description: "Visionnaire et leader naturelle.", description_en: "Visionary and natural leader.", image: '/images/team/marie-laure.jpg' },
          { id: 'mattias', name: 'Mattias Imbert', role: 'CSO & Co-fondateur', role_en: 'CSO & Co-founder', quote: "La stratégie sans exécution n'est qu'une illusion.", quote_en: "Strategy without execution is just an illusion.", description: "Stratège et analyste.", description_en: "Strategist and analyst.", image: '/images/team/mattias.jpg' },
          { id: 'jasmin', name: 'Jasmin Mofette', role: 'CFO & Co-fondateur', role_en: 'CFO & Co-founder', quote: "Les chiffres racontent une histoire.", quote_en: "Numbers tell a story.", description: "Expert financier.", description_en: "Financial expert.", image: '/images/team/jasmin.jpg' },
          { id: 'simohamed', name: 'Simohamed Youssef Benyahia', role: 'CMO & Co-fondateur', role_en: 'CMO & Co-founder', quote: "Le marketing, c'est l'art de raconter la bonne histoire.", quote_en: "Marketing is the art of telling the right story.", description: "Créatif et communicateur.", description_en: "Creative and communicator.", image: '/images/team/simohamed.jpg' },
          { id: 'anne-marie', name: 'Anne-Marie St-Amant', role: 'CDO & Co-fondatrice', role_en: 'CDO & Co-founder', quote: "Le design n'est pas ce à quoi ça ressemble.", quote_en: "Design is not how it looks.", description: "Artiste ingénieure.", description_en: "Artist engineer.", image: '/images/team/anne-marie.jpg' },
          { id: 'lea-mathilde', name: 'Léa-Mathilde Godin', role: 'Lead Mécanique & Co-fondatrice', role_en: 'Mechanical Lead & Co-founder', quote: "La perfection mécanique.", quote_en: "Mechanical perfection.", description: "Experte en mécanique.", description_en: "Mechanical expert.", image: '/images/team/lea-mathilde.jpg' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  // Team carousel navigation
  // CORRECTION 2: On s'assure que teamMembers est un tableau avant de faire slice
  const safeTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
  const visibleTeamMembers = safeTeamMembers.slice(currentTeamIndex, currentTeamIndex + 3);
  const canGoNext = currentTeamIndex + 3 < safeTeamMembers.length;
  const canGoPrev = currentTeamIndex > 0;

  const nextTeam = () => {
    if (canGoNext) setCurrentTeamIndex(prev => prev + 1);
  };

  const prevTeam = () => {
    if (canGoPrev) setCurrentTeamIndex(prev => prev - 1);
  };

  const timelineData = [
    { year: t.about.timeline1Year, title: t.about.timeline1Title, desc: t.about.timeline1Desc },
    { year: t.about.timeline2Year, title: t.about.timeline2Title, desc: t.about.timeline2Desc },
    { year: t.about.timeline3Year, title: t.about.timeline3Title, desc: t.about.timeline3Desc, isHighlight: true },
    { year: t.about.timeline4Year, title: t.about.timeline4Title, desc: t.about.timeline4Desc }
  ];

  const valuesData = [
    { title: t.about.value1Title, icon: <Sparkles />, desc: t.about.value1Desc, color: theme.colors.primary },
    { title: t.about.value2Title, icon: <Target />, desc: t.about.value2Desc, color: theme.colors.secondary },
    { title: t.about.value3Title, icon: <Lightbulb />, desc: t.about.value3Desc, color: theme.colors.accent }
  ];

  return (
    <div className="animate-in fade-in duration-700 bg-black min-h-screen" data-testid="about-page">
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 3s linear infinite;
        }
        @keyframes beam {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .animate-beam {
          animation: beam 3s ease-in-out infinite;
        }
        .perspective-1000 {
          perspective: 1000px;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 10s linear infinite;
        }
      `}</style>

      {/* Header */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top,_rgba(231,165,95,0.15),_transparent_50%)]"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <Reveal>
            {/* Badge matching Solution/Home page style */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-orange-500 font-mono text-sm md:text-lg uppercase tracking-widest font-bold">
                {t.about.headerBadge}
              </span>
              <div className="w-12 h-1 rounded-full bg-orange-500"></div>
            </div>
            {/* Title matching style */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight" style={{ transform: `translate(${parallax.x}px, ${parallax.y}px)` }}>
              <span className="text-white">{t.about.headerTitle1}</span> <span className="text-transparent bg-clip-text" style={{color: theme.colors.primary}}>
                {t.about.headerTitle2}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-light">
              {t.about.headerSubtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission Section */}
      <MissionCore mousePosition={mousePosition} t={t} />

      {/* Values Section */}
      <section className="py-16 md:py-32 bg-[#050505] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold mb-16 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400 text-center">
              {t.about.valuesTitle}
            </h2>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {valuesData.map((value, idx) => (
              <Reveal key={idx} delay={idx * 100}>
                <HolographicValue {...value} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 relative border-t border-gray-900 overflow-hidden">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Reveal>
            <h2 className="text-center text-3xl md:text-5xl font-bold mb-16 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">{t.about.timelineTitle}</h2>
          </Reveal>
          
          <div className="relative ml-4 md:ml-10">
            <div className="absolute left-0 top-2 bottom-0 w-px bg-gray-800"></div>
            <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-orange-500 to-transparent opacity-0 animate-beam"></div>
            
            <div className="space-y-12">
              {timelineData.map((item, idx) => (
                <TimelineItem key={idx} {...item} index={idx + 1} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section with Carousel */}
      <section className="py-24 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-center text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">{t.about.teamTitle}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-lg">{t.about.teamSubtitle}</p>
            </div>
          </Reveal>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="relative">
              {/* Navigation Buttons */}
              <div className="absolute -left-4 md:-left-16 top-1/2 -translate-y-1/2 z-20">
                <button 
                  onClick={prevTeam}
                  disabled={!canGoPrev}
                  className={`p-3 md:p-4 rounded-full border backdrop-blur-md transition-all duration-300 ${
                    canGoPrev 
                      ? 'bg-white/10 border-white/20 hover:bg-orange-500 hover:border-orange-500 hover:text-black cursor-pointer' 
                      : 'bg-white/5 border-white/10 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft size={24} />
                </button>
              </div>
              
              <div className="absolute -right-4 md:-right-16 top-1/2 -translate-y-1/2 z-20">
                <button 
                  onClick={nextTeam}
                  disabled={!canGoNext}
                  className={`p-3 md:p-4 rounded-full border backdrop-blur-md transition-all duration-300 ${
                    canGoNext 
                      ? 'bg-white/10 border-white/20 hover:bg-orange-500 hover:border-orange-500 hover:text-black cursor-pointer' 
                      : 'bg-white/5 border-white/10 text-gray-600 cursor-not-allowed'
                  }`}
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              {/* Team Cards */}
              <div className="grid md:grid-cols-3 gap-8 px-8 md:px-0">
                {/* CORRECTION 3: Ajout du ? avant le map */}
                {visibleTeamMembers?.map((member, idx) => (
                  <Reveal key={member.id || idx} delay={idx * 100}>
                    <TeamCard member={member} language={language} />
                  </Reveal>
                ))}
              </div>

              {/* Pagination Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: Math.ceil(safeTeamMembers.length / 3) }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTeamIndex(idx * 3)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      Math.floor(currentTeamIndex / 3) === idx 
                        ? 'bg-orange-500 w-8' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Social CTA */}
      <section className="py-24 text-center border-t relative overflow-hidden" style={{ borderColor: theme.colors.border }}>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] to-[#111]"></div>
        <Reveal>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-6 text-white">{t.about.socialTitle}</h2>
            <div className="flex justify-center gap-6 mt-10 flex-wrap">
              <SocialButton icon={<Linkedin size={24} />} label="LinkedIn" />
              <SocialButton icon={<Instagram size={24} />} label="Instagram" href={INSTAGRAM_URL} />
              <SocialButton icon={<Twitter size={24} />} label="X (Twitter)" />
              <SocialButton icon={<Facebook size={24} />} label="Facebook" />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};